import { useEffect, useState } from "react";
import { Car, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (ym) => new Date(`${ym}-01T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
const fmtDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

/** Monthly Personal Vehicle report (owner/admin/superadmin): per manager —
 *  day, where, why, +hours — with totals and a CSV download. */
export default function PersonalVehicleReportDialog({ open, onClose }) {
  const [month, setMonth] = useState(monthKey(new Date()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBusy(true);
    api.get(`/personal-vehicle/report?month=${month}`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load the report"); setData(null); })
      .finally(() => setBusy(false));
  }, [open, month]);

  const shift = (n) => { const d = new Date(`${month}-15T12:00:00`); d.setMonth(d.getMonth() + n); setMonth(monthKey(d)); };

  const downloadCsv = () => {
    if (!data) return;
    const rows = [["Manager", "Day", "Destination", "Purpose", "Hours"]];
    data.managers.forEach((m) => m.entries.forEach((e) => rows.push([m.employee_name, e.day, e.destination, e.purpose, e.hours])));
    rows.push([]); rows.push(["TOTAL", "", "", `${data.total_days} day(s)`, data.total_hours]);
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `personal-vehicle-${month}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-[#0b0b0f] text-white" data-testid="vehicle-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm font-bold uppercase tracking-tight">
            <span className="flex h-8 w-8 items-center justify-center rounded-md border border-violet-500/40 bg-violet-500/10 text-violet-300"><Car className="h-4 w-4" /></span>
            Personal vehicle report
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="vehicle-report-prev" className="rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold text-white/80 hover:text-white">‹ Prev</button>
          <span className="min-w-[160px] text-center text-sm font-semibold" data-testid="vehicle-report-month">{monthLabel(month)}</span>
          <button onClick={() => shift(1)} data-testid="vehicle-report-next" className="rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs font-semibold text-white/80 hover:text-white">Next ›</button>
          <div className="flex-1" />
          <button onClick={downloadCsv} disabled={!data?.managers?.length} data-testid="vehicle-report-csv" className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 hover:text-white disabled:opacity-40">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
        </div>

        {busy && <div className="flex items-center gap-2 py-6 text-sm text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {!busy && data && data.managers.length === 0 && (
          <p className="py-6 text-center font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="vehicle-report-empty">No personal vehicle entries in {monthLabel(month)}</p>
        )}
        {!busy && data && data.managers.length > 0 && (
          <div className="space-y-4">
            {data.managers.map((m) => (
              <section key={m.employee_id} className="rounded-lg border border-white/10 bg-white/[0.02]" data-testid={`vehicle-report-manager-${m.employee_id}`}>
                <header className="flex items-center justify-between border-b border-white/10 px-3 py-2">
                  <span className="text-sm font-semibold">{m.employee_name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-violet-300">{m.days} day{m.days === 1 ? "" : "s"} · +{m.hours} h</span>
                </header>
                <ul className="divide-y divide-white/5">
                  {m.entries.map((e) => (
                    <li key={e.entry_id} className="grid grid-cols-[110px_1fr_auto] gap-2 px-3 py-2 text-xs sm:grid-cols-[130px_1fr_1fr_auto]">
                      <span className="text-white/60">{fmtDay(e.day)}</span>
                      <span className="font-semibold text-white">{e.destination}</span>
                      <span className="col-span-2 text-white/70 sm:col-span-1">{e.purpose}</span>
                      <span className="hidden font-mono text-violet-300 sm:block">+{e.hours} h</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            <div className="flex items-center justify-between rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-sm" data-testid="vehicle-report-total">
              <span className="font-semibold">Total</span>
              <span className="font-mono text-violet-200">{data.total_days} day{data.total_days === 1 ? "" : "s"} · +{data.total_hours} h</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
